import { useTranslation } from "react-i18next";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useToast } from "@/shared/hooks/use-toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/shared/components/ui/alert-dialog";
import { Trash2, AlertCircle } from "lucide-react";
import { useState } from "react";
import { InviteService, Invite } from "@/features/Users/services/inviteService";
import { UserRole } from "@/shared/types/user-role";
import { usePagination } from "@/shared/hooks/usePagination";
import { DataPagination } from "@/shared/components/DataPagination";

interface UsersTableProps {
  invites: Invite[];
}

export const UsersTable = ({ invites }: UsersTableProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState<Invite | null>(null);

  const { currentPage, pageSize, setCurrentPage, setPageSize, paginateData } = usePagination({
    initialPage: 1,
    initialPageSize: 10
  });

  const paginatedInvites = paginateData(invites || []);

  const deleteInviteMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      try {
        await InviteService.deleteInvite(inviteId);
      } catch (error) {
        console.error('Erro ao excluir convite:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      queryClient.invalidateQueries({ predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'workspace-invites' });
      toast({
        title: t("users.usersTable.inviteDeleteSuccess"),
        description: t("users.usersTable.inviteDeleteSuccessDescription"),
      });
      setDeleteDialogOpen(false);
      setSelectedInvite(null);
    },
    onError: (error) => {
      console.error('Erro ao excluir convite:', error);
      toast({
        variant: "destructive",
        title: t("users.usersTable.inviteDeleteError"),
        description: t("users.usersTable.inviteDeleteErrorDescription"),
      });
    }
  });

  const handleDeleteClick = (invite: Invite) => {
    setSelectedInvite(invite);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedInvite) {
      deleteInviteMutation.mutate(selectedInvite.id);
    }
  };

  const getInitials = (text: string) => {
    return text[0]?.toUpperCase() || '?';
  };

  const getStatusColor = (status: Invite['status']) => {
    return status === 'Active' ? 'success' : 'warning';
  };

  const getDeleteConfirmMessage = (invite: Invite | null) => {
    if (!invite) return '';
    return invite.status === 'Pending'
      ? t("users.usersTable.deletePendingConfirmDescription")
      : t("users.usersTable.deleteConfirmDescription", { email: invite.email });
  };

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12"></TableHead>
            <TableHead>{t("users.fields.name")}</TableHead>
            <TableHead>{t("users.fields.email")}</TableHead>
            <TableHead>{t("users.fields.role")}</TableHead>
            <TableHead>{t("users.fields.status")}</TableHead>
            <TableHead className="w-[100px]">{t("common.labels.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedInvites.map((invite) => (
            <TableRow key={invite.id}>
              <TableCell>
                <Avatar className="h-8 w-8">
                  <AvatarImage
                    src={invite.avatar_url || ''}
                    alt={invite.user_name || invite.email}
                  />
                  <AvatarFallback>
                    {getInitials(invite.user_name || invite.email)}
                  </AvatarFallback>
                </Avatar>
              </TableCell>
              <TableCell>{invite.user_name || t("users.noName")}</TableCell>
              <TableCell>{invite.email}</TableCell>
              <TableCell>{t(`settings.users.roles.${invite.role || UserRole.USER}`)}</TableCell>
              <TableCell>
                <Badge variant={getStatusColor(invite.status)}>
                  {invite.status}
                </Badge>
              </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteClick(invite)}
                  disabled={deleteInviteMutation.isPending}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {invites && invites.length > 0 && (
        <DataPagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={invites.length}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[5, 10, 20, 50]}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-destructive" />
                {t("users.deleteConfirmTitle")}
              </div>
            </AlertDialogTitle>
            <AlertDialogDescription>
              {getDeleteConfirmMessage(selectedInvite)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.actions.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
